import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";

export default function PaymentSuccessPage({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const tries = useRef(0);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) { /* keep polling */ }
      tries.current += 1;
      if (tries.current < 10) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, cancelled]);

  return (
    <div data-testid="payment-success-page" className="max-w-[720px] mx-auto px-5 py-32 text-center">
      {state === "checking" && (
        <>
          <Loader2 className="mx-auto text-kinetic animate-spin mb-6" size={36} />
          <h1 className="font-display font-extrabold uppercase text-3xl tracking-tight">Confirming your payment…</h1>
          <p className="text-ash mt-4">Hold tight — we're talking to Stripe.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 data-testid="payment-status-paid" className="mx-auto text-kinetic mb-6" size={40} />
          <h1 className="font-display font-extrabold uppercase text-3xl tracking-tight">Payment received.</h1>
          <p className="text-ash mt-4 leading-relaxed">
            Your £10 listing fee is in. Your business card is now in the editors' review queue —
            once approved, it goes live on the directory wall.
          </p>
        </>
      )}
      {state === "cancelled" && (
        <>
          <XCircle className="mx-auto text-crimson mb-6" size={40} />
          <h1 className="font-display font-extrabold uppercase text-3xl tracking-tight">Payment cancelled.</h1>
          <p className="text-ash mt-4 leading-relaxed">
            No charge was made. Your submission is saved — you can complete the listing fee any time
            by submitting again from the directory page.
          </p>
        </>
      )}
      {state === "timeout" && (
        <>
          <Loader2 className="mx-auto text-ash mb-6" size={36} />
          <h1 className="font-display font-extrabold uppercase text-3xl tracking-tight">Still processing…</h1>
          <p className="text-ash mt-4 leading-relaxed">
            Stripe is taking a moment. If you were charged, your listing is safe — check back shortly.
          </p>
        </>
      )}
      <Link to="/directory" data-testid="payment-back-to-directory"
        className="mt-10 inline-block bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium px-7 py-4 rounded-full hover:bg-porcelain transition-colors duration-300">
        Back to the directory
      </Link>
    </div>
  );
}
